/**
 * @fileoverview CategoryRow Component
 *
 * Renders a single category row with labeled category selector, amount input,
 * and action buttons (Add/Remove). Includes validation error display and
 * handles read-only amount for Transfer transactions.
 *
 * @module components/TransactionForm/CategoryRow
 */
import MoneyInput from '../common/MoneyInput';
import styles from './CategoryRow.module.css';

function CategoryRow({
    catItem,
    index,
    transactionType,
    amount,
    availableCategories,
    errors,
    isLastRow,
    canRemove,
    canAdd,
    onCategoryChange,
    clearCategoryError,
    isGrandfatheredCategory,
    isEdit
}) {
    const handleCategorySelect = (value) => {
        onCategoryChange('update', index, 'category', value);
        clearCategoryError(index, 'category');
    };

    const handleAmountChange = (parsedValue) => {
        clearCategoryError(index, 'amount');
        const value = parsedValue !== null ? parsedValue.toString() : '';
        onCategoryChange('update', index, 'amount', value);
    };

    const handleAmountBlur = () => {
        // No special handling needed - MoneyInput handles formatting
    };

    const getCategoryError = () => {
        return errors.categories && errors.categories[index] && errors.categories[index].category;
    };

    const getAmountError = () => {
        return errors.categories && errors.categories[index] && errors.categories[index].amount;
    };

    const renderAmountInput = () => {
        if (transactionType === 'Transfer') {
            return (
                <MoneyInput
                    value={amount ? parseFloat(amount) : null}
                    onChange={() => {}}
                    placeholder="Amount"
                    disabled={true}
                    className={`${styles.amountInputReadOnly} ${getAmountError() ? styles.formControlError : ''}`}
                />
            );
        }

        return (
            <MoneyInput
                value={catItem.amount ? parseFloat(catItem.amount) : null}
                onChange={handleAmountChange}
                onBlur={handleAmountBlur}
                placeholder="Amount"
                min={0}
                max={9999999.99}
                className={`${styles.amountInput} ${getAmountError() ? styles.formControlError : ''}`}
            />
        );
    };

    return (
        <div className={styles.categoryRow}>
            <div className={styles.fieldGroup}>
                <label className={styles.fieldLabel}>Category:</label>
                <select
                    value={catItem.category}
                    onChange={(e) => handleCategorySelect(e.target.value)}
                    className={`${styles.categorySelect} ${getCategoryError() ? styles.formControlError : ''}`}
                >
                    <option value="">Select Category</option>
                    {availableCategories.map(cat => {
                        const isGrandfathered = isEdit && isGrandfatheredCategory && isGrandfatheredCategory(cat, isEdit);
                        return (
                            <option
                                key={cat}
                                value={cat}
                                className={isGrandfathered ? styles.grandfatheredOption : ''}
                            >
                                {cat}
                            </option>
                        );
                    })}
                    {catItem.category && !availableCategories.includes(catItem.category) && (
                        <option
                            key={catItem.category}
                            value={catItem.category}
                            className={isEdit && isGrandfatheredCategory && isGrandfatheredCategory(catItem.category, isEdit) ? styles.grandfatheredOption : ''}
                        >
                            {catItem.category}
                        </option>
                    )}
                </select>
            </div>

            <div className={styles.amountFieldGroup}>
                <div className={styles.fieldGroup}>
                    <label className={styles.fieldLabel}>Amount:</label>
                    {renderAmountInput()}
                </div>
                {canRemove && (
                    <button
                        onClick={() => onCategoryChange('remove', index)}
                        className={styles.removeButton}
                        aria-label="Remove category"
                    >
                        Remove
                    </button>
                )}
            </div>

            {isLastRow && canAdd && (
                <button
                    onClick={() => onCategoryChange('add')}
                    className={styles.addButton}
                >
                    Add Category
                </button>
            )}
        </div>
    );
}

export default CategoryRow;
